import { resolveProjectArtworkPanels } from "@/music-catalog/utils/project-artwork";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * The disc pictures already asked for since the page loaded.
 *
 * A pointer crossing a card a second time, or its link taking focus after the
 * pointer found it, is not a second request. The browser would answer one from
 * its cache anyway; this keeps a card from asking at all.
 */
const warmedDiscUrls = new Set<string>();

/**
 * Ask for a project's disc picture before its page is opened.
 *
 * A list of albums draws each one's Front and nothing else, so the Front is in
 * the browser's cache when the project page opens and the disc is not: the cover
 * landed, and the disc beside it was an empty circle for as long as its bytes
 * took to arrive. Asked for when a reader's pointer or focus reaches the link,
 * the disc is usually there by the time the page draws it.
 *
 * An `Image` rather than a `fetch`: it is the request the page's own `<img>` is
 * about to make, with the same cookie and the same cache entry, and the bytes
 * are immutable under their digest, so it is made once and never again. Only
 * the disc — the Back is not drawn until the reader asks for it.
 */
export const warmProjectDisc = (project: LibraryProject) => {
  const disc = resolveProjectArtworkPanels(project).find((panel) => panel.id === "disc");
  if (disc === undefined || warmedDiscUrls.has(disc.url)) {
    return;
  }
  warmedDiscUrls.add(disc.url);
  new Image().src = disc.url;
};

/**
 * What a link into a project's page spreads onto itself to warm its disc.
 *
 * The pointer arriving and the keyboard arriving, because either is the reader
 * about to open it. A finger has no hover, and its `pointerenter` comes with the
 * press, which is still ahead of the page.
 */
export const resolveProjectDiscWarmUpHandlers = (project: LibraryProject) => {
  const warm = () => warmProjectDisc(project);

  return { onFocus: warm, onPointerEnter: warm };
};
